import { useEffect, useRef, useState } from 'react';
import { apiClient, useAuth } from '../../auth-lib';
import { Modal, Button, Input, Select, useToast } from '../../design-system';
import VoiceButton from '../../components/VoiceButton';
import '../styles/QuickAddTask.css';

// ─── Adding a task while still on the phone ─────────────────────────────────
//
// The full Create Task form (employee/pages/CreateTask.jsx) asks for a client,
// a service, a preparer, a reviewer, assistants, hours and a fee. All of that
// is right for work being planned and wrong for the thing this is: somebody is
// on a call, the client says one more thing, and it has to be written down
// before the call ends. Anything more than a name is too much to ask at that
// moment, so:
//
// ⚠️ ONE REQUIRED FIELD. A title. A due date and a client are offered and both
// are optional — a task with no client is a perfectly good task ("chase ICAI
// for the UDIN"), and refusing to save one would send the person back to the
// long form, which is the thing they were avoiding.
//
// ⚠️ IT ASSIGNS ITSELF TO THE PERSON TYPING. They are noting their own follow-up,
// so they are the preparer. Without it the task lands with no owner and shows up
// on their dashboard only because getMyTasks also matches the creator — visible,
// but on nobody's plate and in nobody's workload.
//
// ⚠️ THE MIC FILLS THE NAME FIELD; IT DOES NOT SUBMIT. `voice` mode (from the
// Android home-screen shortcut) opens this already listening, but what comes
// back lands in the box and waits to be read. Speech adds a hearing mistake on
// top of the model's reading mistake, and the whole design of this feature is
// that a person sees the words before anything is created.

const todayIso = () => new Date().toISOString().slice(0, 10);

function QuickAddTask({ isOpen, onClose, onCreated, voice = false }) {
  const { user } = useAuth();
  const toast = useToast();
  const titleRef = useRef(null);

  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ⚠️ SILENT ON FAILURE, exactly as CreateTask.jsx does it. The client list is
  // a convenience here; a firm whose roles do not let this person list clients
  // still gets a working quick-add, just without the dropdown.
  useEffect(() => {
    if (!isOpen) return;
    apiClient.get('/api/clients')
      .then((res) => setClients(Array.isArray(res.data) ? res.data : []))
      .catch(() => setClients([]));
  }, [isOpen]);

  // Fresh every time it opens — a modal that reopens holding last time's text
  // is one that quietly creates the same task twice.
  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setDueDate('');
    setClientId('');
    setError('');
    // Straight into the one field that matters. Modal moves focus to the panel
    // on open, so this has to run after that.
    const t = setTimeout(() => titleRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [isOpen]);

  // ⚠️ APPENDED, NOT REPLACED. Somebody can type half of it, dictate the rest,
  // or dictate twice because the first attempt trailed off. Overwriting would
  // silently delete what they already had.
  const applyTranscript = (text) => {
    setError('');
    setTitle((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text).slice(0, 200));
    titleRef.current?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault();
    const clean = title.trim();
    if (!clean) {
      setError('Give the task a name');
      titleRef.current?.focus();
      return;
    }
    if (saving) return;

    setSaving(true);
    setError('');
    try {
      const { data } = await apiClient.post('/api/tasks', {
        title: clean,
        // Sent only when set. An empty string would reach the schema as an
        // invalid date and reject the whole task over an optional field.
        ...(dueDate ? { dueDate } : {}),
        // `clientIds` (the array), not `clientId` — createTask reads the array,
        // and an empty one is what "not for any client yet" looks like.
        clientIds: clientId ? [clientId] : [],
        preparerId: user?._id || undefined,
      });
      toast.success(`Added "${clean}"`);
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not add the task';
      setError(msg);
      toast.danger(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add a task"
      size="sm"
      footer={(
        <div className="quick-add-actions">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Add task</Button>
        </div>
      )}
    >
      {/* A real <form>, so the phone keyboard shows "Go" and Enter saves
          without reaching for a button on a screen held in one hand. */}
      <form className="quick-add-form" onSubmit={submit}>
        {/* Above the field it fills, so the order on screen matches the order
            things happen in: speak, read what it heard, then save. */}
        <VoiceButton
          label="Hold to speak"
          autoStart={isOpen && voice}
          disabled={saving}
          onTranscript={applyTranscript}
          onError={(message) => setError(message)}
        />

        <Input
          ref={titleRef}
          label="Task"
          placeholder="e.g. Ask Sharma for the bank statements"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={error || undefined}
          enterKeyHint="go"
          autoComplete="off"
          maxLength={200}
        />

        <Input
          type="date"
          label="Due date"
          helperText="Optional — leave it blank and set one later"
          value={dueDate}
          min={todayIso()}
          onChange={(e) => setDueDate(e.target.value)}
        />

        <Select
          label="Client"
          helperText="Optional — a task does not need one"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
        >
          <option value="">No client</option>
          {clients.map((c) => (
            <option key={c._id} value={c._id}>{c.displayName}</option>
          ))}
        </Select>

        {/* Submits on Enter from any field without showing a second button. */}
        <button type="submit" className="quick-add-submit-proxy" tabIndex={-1} aria-hidden="true" />
      </form>
    </Modal>
  );
}

export default QuickAddTask;
